import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useState, useRef } from 'react';
import { X, Upload, Plus } from 'lucide-react';
import { useNotification } from '../../context/NotificationContext';
import {
 getBilling,
 getReturnDeliveryFee,
 returnItem,
 uploadImage,
} from '../../api/requests';
import { billingData } from '../../interface/interfaces';
import { useNavigate } from 'react-router-dom';
import './returnmodal.scss';

interface ReturnModalProps {
 isOpen: boolean;
 onClose: () => void;
 orderId: string;
 maxQuantity: number;
}

const ReturnModal = ({
 isOpen,
 onClose,
 orderId,
 maxQuantity,
}: ReturnModalProps) => {
 const { notify } = useNotification();
 const navigate = useNavigate();
 const [step, setStep] = useState(1);
 const [isLoading, setIsLoading] = useState(false);
 const [addresses, setAddresses] = useState<billingData[]>([]);
 const [selectedAddress, setSelectedAddress] = useState<string>('');
 const [deliveryFee, setDeliveryFee] = useState<number | null>(null);
 const [returnReason, setReturnReason] = useState('');
 const [quantity, setQuantity] = useState(1);
 const [documents, setDocuments] = useState<string[]>([]);
 const [isUploading, setIsUploading] = useState(false);

 useEffect(() => {
  if (isOpen) {
   loadAddresses();
  }
  // eslint-disable-next-line
 }, [isOpen]);

 const loadAddresses = async () => {
  try {
   setIsLoading(true);
   const data = await getBilling();
   setAddresses(data);
  } catch (error: any) {
   notify.error(error.message);
  } finally {
   setIsLoading(false);
  }
 };

 const handleAddressSelect = (id: string) => {
  setSelectedAddress(id);
 };

 const isSubmittingRef = useRef(false);

 const handleNextStep = async () => {
  if (isSubmittingRef.current) return;

  if (step === 1) {
   if (!selectedAddress) {
    notify.error('Please select a pickup address');
    return;
   }
   try {
    isSubmittingRef.current = true;
    setIsLoading(true);
    const res = await getReturnDeliveryFee(orderId, {
     pickup_address: selectedAddress,
    });
    setDeliveryFee(res.delivery_price);
    setStep(2);
   } catch (error: any) {
    notify.error(error.message);
   } finally {
    setIsLoading(false);
    isSubmittingRef.current = false;
   }
  }
 };

 const handleFileUpload = async (
  e: React.ChangeEvent<HTMLInputElement>,
 ) => {
  if (e.target.files && e.target.files[0]) {
   const file = e.target.files[0];
   const formData = new FormData();
   formData.append('file', file);
   formData.append('upload_preset', process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET!);

   try {
    setIsUploading(true);
    const url = await uploadImage(process.env.REACT_APP_CLOUDINARY_CLOUD_NAME!, formData);
    setDocuments((prev) => [...prev, url]);
   } catch (error: any) {
    notify.error('Failed to upload image');
   } finally {
    setIsUploading(false);
   }
  }
 };

 const handleSubmit = async () => {
  if (isSubmittingRef.current) return;

  if (!returnReason.trim()) {
   notify.error('Please enter a return reason');
   return;
  }
  if (quantity < 1 || quantity > maxQuantity) {
   notify.error(`Quantity must be between 1 and ${maxQuantity}`);
   return;
  }

  try {
   isSubmittingRef.current = true;
   setIsLoading(true);
   const payload = {
    pickup_address: selectedAddress,
    return_reason: returnReason,
    documents: documents,
    quantity: quantity,
    delivery_fee: deliveryFee,
   };

   const res = await returnItem(orderId, payload);
   if (res.transaction_details?.authorization_url) {
    window.location.href = res.transaction_details.authorization_url;
   } else {
    notify.success('Return request submitted successfully');
    onClose();
   }
  } catch (error: any) {
   notify.error(error.message);
  } finally {
   setIsLoading(false);
   isSubmittingRef.current = false;
  }
 };

 return (
  <AnimatePresence>
   {isOpen && (
    <motion.div
     className="return-modal-overlay"
     initial={{ opacity: 0 }}
     animate={{ opacity: 1 }}
     exit={{ opacity: 0 }}
    >
     <motion.div
      className="return-modal"
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.9, opacity: 0 }}
     >
      <button
       className="close-btn"
       onClick={onClose}
      >
       <X size={24} />
      </button>

      <div className="modal-header">
       <h2>Return Item</h2>
       <div className="steps">
        <div className={`step ${step >= 1 ? 'active' : ''}`}>1</div>
        <div className="line"></div>
        <div className={`step ${step >= 2 ? 'active' : ''}`}>2</div>
       </div>
      </div>

      <div className="modal-content">
       {step === 1 && (
        <div className="step-content">
         <h3>Select Pickup Address</h3>
         <div className="address-list">
          {addresses.map((addr) => (
           <div
            key={addr.id}
            className={`address-item ${
             selectedAddress === addr.id ? 'selected' : ''
            }`}
            onClick={() => handleAddressSelect(addr.id)}
           >
            <div className="addr-details">
             <span className="name">{addr.name}</span>
             <span className="text">{addr.billing_address}</span>
             <span className="phone">{addr.billing_phone}</span>
            </div>
            <div className="radio">
             {selectedAddress === addr.id && (
              <div className="dot"></div>
             )}
            </div>
           </div>
          ))}
         </div>
         <button
          className="add-addr-btn"
          onClick={() => navigate('/account/address')}
         >
          <Plus size={16} /> Add New Address
         </button>
        </div>
       )}

       {step === 2 && (
        <div className="step-content">
         <h3>Return Details</h3>
         <div className="fee-info">
          <span>Delivery Fee:</span>
          <span className="amount">
           ₦{deliveryFee?.toLocaleString()}
          </span>
         </div>

         <div className="form-group">
          <label>Quantity</label>
          <div className="qty-control">
           <button
            onClick={() => setQuantity((q) => Math.max(1, q - 1))}
            disabled={quantity <= 1}
           >
            -
           </button>
           <span>{quantity}</span>
           <button
            onClick={() =>
             setQuantity((q) => Math.min(maxQuantity, q + 1))
            }
            disabled={quantity >= maxQuantity}
           >
            +
           </button>
          </div>
         </div>

         <div className="form-group">
          <label>Reason for Return</label>
          <textarea
           value={returnReason}
           onChange={(e) => setReturnReason(e.target.value)}
           placeholder="Please describe why you are returning this item..."
          ></textarea>
         </div>

         <div className="form-group">
          <label>Upload Documents (Optional)</label>
          <div className="upload-area">
           {documents.map((doc, idx) => (
            <div
             key={idx}
             className="doc-preview"
            >
             <img
              src={doc}
              alt="doc"
             />
             <button
              onClick={() =>
               setDocuments((docs) =>
                docs.filter((_, i) => i !== idx),
               )
              }
             >
              <X size={12} />
             </button>
            </div>
           ))}
           <label className="upload-btn">
            <input
             type="file"
             accept="image/*"
             onChange={handleFileUpload}
             hidden
            />
            {isUploading ? (
             <div className="spinner"></div>
            ) : (
             <Upload size={20} />
            )}
           </label>
          </div>
         </div>
        </div>
       )}
      </div>

      <div className="modal-footer">
       {step === 1 ? (
        <button
         className="btn primary"
         onClick={handleNextStep}
         disabled={isLoading || !selectedAddress}
        >
         {isLoading ? 'Loading...' : 'Next'}
        </button>
       ) : (
        <div className="btn-group">
         <button
          className="btn secondary"
          onClick={() => setStep(1)}
          disabled={isLoading}
         >
          Back
         </button>
         <button
          className="btn primary"
          onClick={handleSubmit}
          disabled={isLoading}
         >
          {isLoading ? 'Processing...' : 'Pay & Return'}
         </button>
        </div>
       )}
      </div>
     </motion.div>
    </motion.div>
   )}
  </AnimatePresence>
 );
};

export default ReturnModal;
